import React, { ReactElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Trash2 } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Checkbox } from '@ui/components/ui/checkbox';
import { Dialog, DialogContent, DialogTitle } from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { Textarea } from '@ui/components/ui/textarea';

import { clientPath } from '@app/constants/url';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { Roles } from '@app/utils/types';
import {
  MembershipStatus,
  ProductStatus,
  useAddClientCompanyPerson,
  useCreateClientCompany,
  useCreateProduct,
  useListClientCompanies,
  useListProducts,
  useUpdateClientCompany,
} from '@data';
import { useListPmMembers } from '@data/hooks/membership';

import { reasonOf } from '@data/utils/errors';
import {
  ConnectAppsRows,
  connectionsToPayload,
  mergeConnections,
  ProductConnections,
} from '../../products/productDetail/ConnectedApps';
import { toProductUrl } from '../../products/productUrl';
import ClientAccessPanel, { DraftPerson } from './ClientAccessPanel';

const STATUS_ORDER: ProductStatus[] = [
  ProductStatus.active,
  ProductStatus.support,
  ProductStatus.deactivated,
  ProductStatus.archived,
];

const Req = (): ReactElement => <span className="text-destructive">*</span>;

interface AddClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** The signed-in person's role: only an admin may create a product, so a manager skips that step. */
  role?: string;
}

const nameOf = (person: { firstName?: string; lastName?: string }): string =>
  `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();

/**
 * The product being created stands in for its own id until it has one.
 *
 * The access panel picks products by id, and a client this new has exactly one — the product step's, if
 * it was filled in. So the draft carries this in its place and `finish` swaps it for the real id.
 */
const DRAFT_PRODUCT_ID = 'draft-product';

/**
 * Guided "Add client": the company → a product for them → the people who will read it.
 *
 * Three steps rather than one form because that is the order the questions actually arrive in, and each
 * later step is optional: a client can be entered with nothing else known. The dialog's height tweens
 * between steps so the tall product form and the short name step do not snap into each other.
 *
 * The client is written **first**, so a name already taken is refused before anything else exists. The
 * product and the people follow, each attached to it.
 */
const AddClientDialog = ({ open, onOpenChange, organizationId, role }: AddClientDialogProps): ReactElement => {
  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();

  const createClient = useCreateClientCompany(organizationId);
  // Both are given an empty client and the real one per call: the client does not exist until Finish.
  const updateClient = useUpdateClientCompany(organizationId, '');
  const addPersonMutation = useAddClientCompanyPerson(organizationId, '');
  const createProduct = useCreateProduct(organizationId);
  const { data: clients } = useListClientCompanies(organizationId);
  // Only people who still work here are offered, which is also what the API accepts.
  const { data: membersData } = useListPmMembers(organizationId, { userStatus: MembershipStatus.active });

  // Creating a product is admin-only in the API, so a manager's flow goes straight from the name to the
  // people — offering a step that would be refused is worse than not offering it.
  const canCreateProduct = role === Roles.admin;

  // Every status, which is what the API compares a new product's name against.
  const { data: products } = useListProducts(canCreateProduct ? organizationId : undefined);

  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [people, setPeople] = useState<DraftPerson[]>([]);

  const [productName, setProductName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<ProductStatus>(ProductStatus.active);
  const [billable, setBillable] = useState('yes');
  const [team, setTeam] = useState<string[]>([]);
  const [managers, setManagers] = useState<string[]>([]);
  const [rnd, setRnd] = useState<Record<string, string>>({});
  const [connections, setConnections] = useState<ProductConnections>({ repositories: [] });

  const [saving, setSaving] = useState(false);

  // The same fade the Add-product dialog carries, for the same reason: the product step is taller than
  // the window and would otherwise look as though it ends at the fold.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const syncScroll = (): void => {
    const element = scrollRef.current;
    if (element) setAtBottom(element.scrollHeight - element.scrollTop - element.clientHeight < 4);
  };
  const [failed, setFailed] = useState<string | null>(null);
  const [productFailed, setProductFailed] = useState<string | null>(null);
  // What the last attempt did manage to create. Kept so a second Finish attaches to those rather than
  // asking again for a client whose name is now taken by itself, or a product that already exists.
  const createdClientId = useRef<string | null>(null);
  const createdProductId = useRef<string | null>(null);
  // Whether that product has been attached to the client yet. Separate from having created it, because
  // the two are separate writes and the second can fail on its own.
  const productLinked = useRef(false);

  const trimmedName = name.trim();
  const existingNames = (clients?.items ?? []).map(client => client.name.toLowerCase());
  // Once the client is there, the list holds it too, and it would otherwise be reported as a clash with
  // itself. The name is settled at that point, which is why the field is locked rather than re-checked.
  const clientCreated = Boolean(createdClientId.current);
  const nameDuplicate = !clientCreated && !!trimmedName && existingNames.includes(trimmedName.toLowerCase());
  const nameError = nameDuplicate ? t('addClientDialog.duplicateName') : null;
  const canContinueName = !!trimmedName && !nameDuplicate;

  // Checked while it is typed rather than only on Finish: the client is written first, so a product name
  // already taken used to be refused with a client already created and nothing on screen saying so.
  const trimmedProductName = productName.trim();
  const productNames = (products?.items ?? []).map(product => product.name.toLowerCase());
  const productDuplicate = !!trimmedProductName && productNames.includes(trimmedProductName.toLowerCase());
  const productError = productDuplicate ? t('addProduct.nameTaken') : productFailed;

  // What the people step can share: the product this client is being given, while it is still a name.
  const draftProducts = useMemo(
    () => (canCreateProduct && trimmedProductName ? [{ id: DRAFT_PRODUCT_ID, name: trimmedProductName }] : []),
    [canCreateProduct, trimmedProductName],
  );

  const members = useMemo(() => membersData?.items ?? [], [membersData]);
  const memberOptions = members
    .filter(membership => membership.role !== Roles.client)
    .filter(membership => !team.includes(membership.id))
    .map(membership => ({ value: membership.id, label: nameOf(membership.user) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Height animation: measure the content and drive the container's height, so a step change tweens
  // rather than snapping. Measured from a ref callback because the content mounts with the dialog — an
  // effect keyed on `open` can run either side of that and leave a stale height behind on reopen.
  const innerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [dialogHeight, setDialogHeight] = useState<number>();
  const attachInner = useCallback((node: HTMLDivElement | null): void => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    innerRef.current = node;
    if (!node) {
      setDialogHeight(undefined);
      return;
    }
    setDialogHeight(node.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return;
    // Follows height changes within a step too, such as a team row being added.
    observerRef.current = new ResizeObserver(() => setDialogHeight(node.offsetHeight));
    observerRef.current.observe(node);
  }, []);
  useEffect(() => () => observerRef.current?.disconnect(), []);
  useLayoutEffect(() => {
    if (innerRef.current) setDialogHeight(innerRef.current.offsetHeight);
    // Measured before paint: the step animation moves opacity and transform only, so the fade would
    // otherwise appear a frame late.
    syncScroll();
  }, [step, open]);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setName('');
    setPeople([]);
    setProductName('');
    setDescription('');
    setUrl('');
    setStatus(ProductStatus.active);
    setBillable('yes');
    setTeam([]);
    setManagers([]);
    setRnd({});
    setConnections({ repositories: [] });
    setFailed(null);
    setProductFailed(null);
    createdClientId.current = null;
    createdProductId.current = null;
    productLinked.current = false;
  }, [open]);

  const finish = async (): Promise<void> => {
    setSaving(true);
    setFailed(null);
    setProductFailed(null);
    try {
      // The client first: a name already taken is refused here, before a product or an account exists.
      const client = createdClientId.current
        ? { id: createdClientId.current }
        : await createClient.mutateAsync({ name: trimmedName });
      createdClientId.current = client.id;

      if (canCreateProduct && productName.trim()) {
        // Remembered the moment it exists, not once it is linked: attaching it is a second write, and a
        // retry that had forgotten the product would ask for it again, be refused the name it already
        // has, and leave the first one orphaned behind a message blaming the name.
        if (!createdProductId.current) {
          const product = await createProduct.mutateAsync({
            name: productName.trim(),
            status,
            description: description.trim() || undefined,
            url: toProductUrl(url) || undefined,
            billable: billable === 'yes',
            team: team.map(membershipId => ({
              membershipId,
              isManager: managers.includes(membershipId),
              rndShare: Number(rnd[membershipId] ?? 100),
            })),
            ...connectionsToPayload(connections),
          });
          createdProductId.current = product.id;
        }
        if (!productLinked.current) {
          await updateClient.mutateAsync({ clientId: client.id, productIds: [createdProductId.current] });
          productLinked.current = true;
        }
      }

      // Sequentially, so one failing address does not race the others into a half-written client. Each
      // one that lands comes off the list, so a retry after a failure adds only the rest.
      await people.reduce(
        (previous, person) =>
          previous.then(() =>
            addPersonMutation
              .mutateAsync({
                clientId: client.id,
                firstName: person.firstName,
                lastName: person.lastName || undefined,
                email: person.email,
                role: person.role,
                isSuperAdmin: person.isSuperAdmin,
                // The stand-in the people step picked becomes the product that was just created. It is
                // dropped when there is none, rather than sent as an id the API would refuse.
                productIds: person.productIds.flatMap(id => {
                  if (id !== DRAFT_PRODUCT_ID) return [id];
                  return createdProductId.current ? [createdProductId.current] : [];
                }),
              })
              .then(() => {
                setPeople(current => current.filter(item => item.email !== person.email));
              }),
          ),
        Promise.resolve(),
      );

      onOpenChange(false);
      transitionNavigate(clientPath(organizationId, client.id), 'forward');
    } catch (error) {
      const reason = reasonOf(error);
      // Nothing was written yet, so the name step is where this belongs.
      if (!createdClientId.current) {
        setFailed(reason === 'clientNameTaken' ? t('addClientDialog.duplicateName') : t('addClientFailed'));
        setStep(1);
        return;
      }
      // The client is already there, so saying nothing was created would be a lie, and sending whoever is
      // typing back to the name step only makes them fight their own client over the name.
      if (reason === 'productNameTaken') {
        setProductFailed(t('addProduct.nameTaken'));
        setStep(2);
        return;
      }
      // The one refusal from the people step worth naming: it says which person to change, where the
      // generic message only says to try again — and trying again would be refused the same way.
      setFailed(
        reason === 'superAdminExists' ? t('addClientDialog.superAdminExistsOnFinish') : t('addClientDialog.restFailed'),
      );
      setStep(3);
    } finally {
      setSaving(false);
    }
  };

  const renderTeamRow = (membershipId: string): ReactElement => {
    const membership = members.find(item => item.id === membershipId);
    return (
      <div key={membershipId} className="flex items-center gap-4 px-3 py-2">
        <span className="min-w-0 flex-1 truncate text-sm">{nameOf(membership?.user ?? {})}</span>
        <span className="w-16 shrink-0 text-center">
          <Checkbox
            aria-label={`${nameOf(membership?.user ?? {})}, ${t('manager')}`}
            checked={managers.includes(membershipId)}
            onCheckedChange={checked =>
              setManagers(current => (checked ? [...current, membershipId] : current.filter(id => id !== membershipId)))
            }
          />
        </span>
        <span className="w-24 shrink-0">
          <Input
            aria-label={`${nameOf(membership?.user ?? {})}, ${t('rnd')}`}
            value={rnd[membershipId] ?? '100'}
            onChange={event => setRnd(current => ({ ...current, [membershipId]: event.target.value }))}
            inputMode="numeric"
            className="h-8 bg-popover"
          />
        </span>
        <span className="w-8 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${t('actions.remove')} ${nameOf(membership?.user ?? {})}`}
            onClick={() => {
              setTeam(current => current.filter(id => id !== membershipId));
              setManagers(current => current.filter(id => id !== membershipId));
            }}
          >
            <Trash2 className="size-4" />
          </Button>
        </span>
      </div>
    );
  };

  const addMemberField = (
    <MultiSelectField
      label={<span className="sr-only">{t('addPerson')}</span>}
      options={memberOptions}
      value={[]}
      onChange={vals => {
        const [added] = vals;
        if (added) setTeam(current => [...current, added]);
      }}
      emptyLabel={t('addPerson')}
      searchPlaceholder={t('search')}
      emptySearchLabel={t('noMatches')}
      className="max-w-xs"
    />
  );

  const content: Record<number, ReactElement> = {
    1: (
      <div>
        <h2 className="text-xl font-bold text-foreground">{t('addClientDialog.clientStepTitle')}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t('addClientDialog.clientStepHint')}</p>
        <div className="mt-5">
          <Input
            autoFocus
            value={name}
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && canContinueName) setStep(canCreateProduct ? 2 : 3);
            }}
            placeholder={t('addClientDialog.companyNamePlaceholder')}
            aria-label={t('titles.name')}
            aria-invalid={Boolean(nameError)}
            disabled={clientCreated}
          />
          {nameError && <p className="mt-1 text-sm text-destructive">{nameError}</p>}
          {failed && <p className="mt-1 text-sm text-destructive">{failed}</p>}
        </div>
      </div>
    ),
    2: (
      <div>
        <h2 className="text-xl font-bold text-foreground">{t('addClientDialog.productStepTitle')}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('addClientDialog.productStepHint', { name: trimmedName || t('addClientDialog.thisClient') })}
        </p>
        <div className="mt-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>
              {t('titles.name')} <Req />
            </Label>
            <Input
              value={productName}
              onChange={event => {
                setProductName(event.target.value);
                setProductFailed(null);
              }}
              aria-label={t('titles.product')}
              aria-invalid={Boolean(productError)}
              className="max-w-md bg-popover"
            />
            {productError && <p className="text-sm text-destructive">{productError}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('client')}</Label>
            {/* Locked to the client being created — that is the point of doing it here. */}
            <Input value={trimmedName} disabled className="max-w-md bg-muted" />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>
              {t('titles.productDescription')}{' '}
              <span className="font-normal text-muted-foreground">({t('optional')})</span>
            </Label>
            <Textarea
              value={description}
              onChange={event => setDescription(event.target.value)}
              rows={3}
              className="bg-popover"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>
              {t('productDetail.address')} <span className="font-normal text-muted-foreground">({t('optional')})</span>
            </Label>
            <Input
              value={url}
              onChange={event => setUrl(event.target.value)}
              className="max-w-md bg-popover"
              inputMode="url"
            />
          </div>

          <MultiSelectField
            single
            label={
              <>
                {t('status')} <Req />
              </>
            }
            options={STATUS_ORDER.map(value => ({ value, label: t(`tabs.${value}`) }))}
            value={[status]}
            onChange={vals => setStatus((vals[0] as ProductStatus) ?? status)}
            emptyLabel={t('status')}
            searchable={false}
            clearable={false}
            className="max-w-xs"
          />

          <div className="flex flex-col gap-1.5">
            <Label>
              {t('billableWork')} <Req />
            </Label>
            <div className="inline-flex w-fit rounded-lg border border-input bg-popover p-0.5">
              {['yes', 'no'].map(value => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setBillable(value)}
                  className={cx(
                    'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                    billable === value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(value)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>
              {t('teamMembers')} <span className="font-normal text-muted-foreground">({t('optional')})</span>
            </Label>
            {team.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-border">
                <div className="min-w-[30rem]">
                  <div className="flex items-center gap-4 border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <span className="min-w-0 flex-1">{t('member')}</span>
                    <span className="w-16 shrink-0 text-center">{t('manager')}</span>
                    <span className="w-24 shrink-0">{t('rnd')}</span>
                    <span className="w-8 shrink-0" />
                  </div>
                  <div className="divide-y divide-border">{team.map(renderTeamRow)}</div>
                  <div className="border-t border-border p-2">{addMemberField}</div>
                </div>
              </div>
            ) : (
              addMemberField
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>
              {t('connectedApps.title')} <span className="font-normal text-muted-foreground">({t('optional')})</span>
            </Label>
            <ConnectAppsRows
              product={connections}
              productName={productName}
              source={{ organizationId }}
              canEdit
              isPending={saving}
              patch={values => setConnections(current => mergeConnections(current, values))}
            />
          </div>
        </div>
      </div>
    ),
    3: (
      <div>
        <h2 className="text-xl font-bold text-foreground">{t('addClientDialog.usersStepTitle')}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('addClientDialog.usersStepHint', { name: trimmedName || t('addClientDialog.thisClient') })}
        </p>
        <div className="mt-5 flex flex-col gap-4">
          {/* The same panel the client's own page carries, over a draft: what is picked here is written
              when the guided add finishes, against the client and the product it creates. */}
          <ClientAccessPanel products={draftProducts} people={people} onChange={setPeople} />
          {failed && <p className="text-sm text-destructive">{failed}</p>}
        </div>
      </div>
    ),
  };

  const cancel = (
    <Button variant="outline" onClick={() => onOpenChange(false)}>
      {t('actions.cancel')}
    </Button>
  );

  const footer: Record<number, ReactElement> = {
    1: (
      <>
        {cancel}
        <Button disabled={!canContinueName} onClick={() => setStep(canCreateProduct ? 2 : 3)}>
          {t('continue')}
          <ArrowRight className="size-4" />
        </Button>
      </>
    ),
    2: (
      <>
        {cancel}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setProductName('');
              setProductFailed(null);
              setStep(3);
            }}
          >
            {t('skipForNow')}
          </Button>
          {/* `productFailed` too: a name the cached list does not know about is only refused by the API, and
              Continue staying enabled let that rejection be walked past straight back into itself. */}
          <Button disabled={!trimmedProductName || productDuplicate || !!productFailed} onClick={() => setStep(3)}>
            {t('continue')}
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </>
    ),
    3: (
      <>
        {cancel}
        <Button disabled={saving} onClick={() => finish().catch(() => undefined)}>
          {people.length ? t('addClient') : t('addClientDialog.withoutUsers')}
        </Button>
      </>
    ),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        style={{ height: dialogHeight }}
        className={cx(
          // Placed from the top on a wide screen only; a phone keeps the sheet on its bottom edge.
          'block overflow-hidden p-0 sm:translate-y-0',
          // Step 1 is the first thing shown, so it must not animate on open; everything after it does.
          step > 1 && 'transition-[height,top] duration-300 ease-out',
          step === 1 && 'sm:top-[20vh] sm:max-w-md',
          step === 2 && 'sm:top-[7vh] sm:max-w-3xl',
          step === 3 && 'sm:top-[20vh] sm:max-w-3xl',
        )}
      >
        <DialogTitle className="sr-only">{t('addClient')}</DialogTitle>
        {/* A plain block child, so its measured height is its natural content height and the animated
            parent above cannot feed back into it. */}
        <div ref={attachInner} className={cx('flex flex-col p-4', step === 2 ? 'max-h-[86vh]' : 'max-h-[72vh]')}>
          {step > 1 && (
            <button
              type="button"
              // Mirrors the forward gating: a manager never saw the product step, so back skips it too.
              onClick={() => setStep(step === 3 && !canCreateProduct ? 1 : step - 1)}
              className="-ml-1 mb-1 inline-flex w-fit shrink-0 items-center gap-1 rounded-md p-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="size-4" />
              {t('back')}
            </button>
          )}
          <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              key={step}
              ref={scrollRef}
              onScroll={syncScroll}
              className={cx(
                '-mx-1 min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1 pb-1 duration-300 ease-out animate-in fade-in-0',
                step > 1 && 'slide-in-from-right-6',
              )}
            >
              {content[step]}
            </div>
            {!atBottom && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-popover to-transparent" />
            )}
          </div>
          <div className="mt-4 flex shrink-0 items-center justify-between gap-3">{footer[step]}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddClientDialog;
